// CB:BEGIN pm-project-agreement-modal
/**
 * PMProjectAgreementModal
 *
 * Full-screen blocking modal shown when a PM selects a contractor.
 * PM must scroll through, check, and type their name before awarding.
 * Calls POST /api/projects/:id/pm-project-agreement/sign on submit.
 * On success, calls onSigned() which should proceed to bid acceptance.
 */
import React, { useState, useRef, useCallback } from 'react';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import { X } from 'lucide-react';

const BRAND_GREEN = '#123c3b';
const PM_PROJECT_AGREEMENT_VERSION = '1';

const PM_PROJECT_AGREEMENT_TEXT = `COREBIDS PROPERTY MANAGER PROJECT-SPECIFIC AGREEMENT

Version 1 | Effective Date: April 9, 2026

PARTIES

This Property Manager Project-Specific Agreement ("Project Agreement") is entered into between CoreBids Inc. ("CoreBids"), a Delaware C corporation, and the Property Manager identified below ("PM"), governing the specific project identified in the CoreBids Platform at the time of acceptance.

1. PURPOSE AND INCORPORATION BY REFERENCE

This Project Agreement governs the specific project awarded through the CoreBids Platform ("Project"). By accepting this Project Agreement, PM acknowledges having reviewed and approved the scope of work, bid, and project information submitted through the Platform. The accepted bid, project description, any attached quotes or invoices, and the CoreBids Property Manager Platform Agreement are incorporated by reference.

2. PM AUTHORITY AND REPRESENTATIONS

PM represents and warrants that: (a) PM has full legal authority to request, commission, authorize, and fund exterior repair or maintenance work at the project property; (b) PM has obtained all required approvals from property owners, homeowners' associations (HOA), or other required parties; (c) all lease provisions, owner agreements, or management agreements permit the requested work; (d) all project information, property descriptions, addresses, measurements, photos, reports, and scope details submitted to the Platform are accurate and complete to PM's knowledge; and (e) PM will promptly update the Platform if any submitted information is discovered to be inaccurate.

Each submission of a project constitutes a renewed representation of the above warranties for that specific project and property. Misrepresentation of PM's authority or the accuracy of project information is a material breach subject to immediate suspension and potential liability.

3. COREBIDS ROLE — MARKETPLACE AND PAYMENT FACILITATION ONLY

CoreBids operates exclusively as an online marketplace and payment facilitation platform. CoreBids is not a licensed contractor, construction manager, general contractor, design professional, architect, engineer, building official, or insurance provider. CoreBids does not: supervise, direct, or control construction work; verify contractor licensing, bonding, or insurance in real time; guarantee workmanship, code compliance, or project outcomes; guarantee contractor performance, responsiveness, or completion timing; or act as a party to the construction contract between PM and Contractor.

All construction obligations, warranties, and performance duties run directly between PM and Contractor under the project contract formed upon escrow funding. CoreBids' role is limited to operating the platform, facilitating payments, and administering platform-level rules.

4. BINDING PROJECT FORMATION

A binding project relationship between PM and Contractor is formed when: (a) PM awards the project by selecting a contractor's bid through the Platform; (b) PM executes and submits this Project Agreement; and (c) PM funds the required escrow amount through the CoreBids/Stripe payment system.

Upon full escrow funding, PM and Contractor are bound under the accepted bid scope and the platform rules. CoreBids is not a party to the underlying construction contract except to the extent of facilitating platform rules, payments, and dispute resolution.

5. ESCROW-LIKE PAYMENT FACILITATION AND MILESTONES

All project payments must be processed exclusively through the CoreBids/Stripe payment system. PM agrees to fund the full required milestone amounts, not make any off-platform payments, authorize milestone releases through the Platform when milestone work is confirmed complete, and acknowledge that CoreBids is not a bank, regulated escrow company, or licensed money transmitter.

The standard milestone schedule is approximately 25% upon project start (M1), 25% at mid-project (M2), and 50% upon substantial completion (M3). CoreBids may pause disbursements pending dispute review.

6. PROPERTY ACCESS, OCCUPANTS, AND SITE CONDITIONS

PM is solely responsible for providing the Contractor with adequate, safe, and timely access to the project property; notifying tenants and occupants; securing pets and removing personal property from work areas; ensuring utilities are available; and disclosing known hazardous conditions or access restrictions. PM is liable for contractor delays caused by lack of access or inaccurate site information.

7. CONTRACTOR INDEPENDENCE — NO COREBIDS WARRANTY

The Contractor is an independent contractor. CoreBids does not guarantee the Contractor's licensing, insurance, workmanship, code compliance, or completion timing. PM is responsible for its own due diligence. Workmanship disputes run directly between PM and Contractor.

8. MEASUREMENT, REPORT, AND SCOPE DISCLAIMER

Any third-party measurement reports or estimates are informational only. The Contractor is responsible for independent field verification. Hidden conditions may require change orders after work begins.

9. CHANGE ORDERS

No verbal modifications. All scope/price/time changes must be documented in a written change order approved through the CoreBids Platform workflow or signed by both parties and submitted to CoreBids. Emergency work must be documented within 24 hours.

10. DISPUTES, ESCROW HOLDS, AND PLATFORM AUTHORITY

In disputes, either party must notify CoreBids through the Platform. CoreBids may pause milestone disbursements. Both parties must cooperate with document requests. CoreBids has final platform-level authority over escrowed funds.

11. ANTI-CIRCUMVENTION AND NON-SOLICITATION

PM may not solicit or hire any Contractor introduced through CoreBids outside the Platform for 365 days after first introduction. Violation is a material breach. Liquidated damages: the greater of 15% of the off-platform project value or $2,500, plus equitable relief.

12. LIMITATION OF LIABILITY

CoreBids' aggregate liability is capped at fees paid for this specific project. CoreBids is not liable for indirect, consequential, or punitive damages, or for Contractor failures, PM access failures, or Force Majeure events.

13. INDEMNIFICATION

PM indemnifies CoreBids against claims arising from PM's breach, lack of authority, tenant/HOA/owner claims, inaccurate property information, access failures, and anti-circumvention violations.

14. ELECTRONIC SIGNATURE AND AUDIT TRAIL

PM's checkbox acceptance, typed name, and button click constitute a valid electronic signature under E-SIGN and applicable UETA statutes. CoreBids maintains an audit trail of PM's name, timestamp, IP, user-agent, project ID, and version. PM consents to electronic delivery.

15. GOVERNING LAW AND ARBITRATION

Governed by North Carolina law. Unresolved disputes go to binding AAA arbitration in Nashville, Tennessee. Prevailing party recovers attorneys' fees. CLASS ACTIONS WAIVED.

16. SURVIVAL

Sections 2, 11, 12, 13, 14, and 15 survive project completion, cancellation, or termination.

17. ACCEPTANCE

By accepting this Project Agreement electronically, PM confirms PM has read, understood, and agrees to be bound by all terms. This Agreement is effective as of PM's electronic acceptance timestamp in the CoreBids audit log.

CoreBids Property Manager Project-Specific Agreement | Version 1 | April 9, 2026`;

interface PMProjectAgreementModalProps {
  projectId: string;
  projectAddress?: string;
  onSigned: () => void;
  onCancel: () => void;
}

const PMProjectAgreementModal: React.FC<PMProjectAgreementModalProps> = ({
  projectId,
  projectAddress,
  onSigned,
  onCancel,
}) => {
  const authFetch = useAuthFetch();
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (atBottom) setScrolledToBottom(true);
  }, []);

  const canSign = scrolledToBottom && agreed && typedName.trim().length >= 2;

  const handleSign = async () => {
    if (!canSign) return;
    setError(null);
    setSaving(true);
    try {
      const resp = await authFetch(getApiUrl(`/api/projects/${projectId}/pm-project-agreement/sign`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          typedName: typedName.trim(),
          signerPosition: signerPosition.trim() || undefined,
          version: PM_PROJECT_AGREEMENT_VERSION,
        }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to sign agreement');
      }
      onSigned();
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6,
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', padding: '32px 16px 48px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, border: '1.5px solid #e2e8f0',
        padding: '36px 40px 32px', maxWidth: 680, width: '100%',
        boxShadow: '0 8px 48px rgba(0,0,0,0.22)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: BRAND_GREEN, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              Legal Step Required
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>
              PM Project-Specific Agreement
            </h2>
            {projectAddress && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{projectAddress}</div>
            )}
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Agreement version badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ background: '#f0fdf4', color: '#166534', borderRadius: 16, padding: '3px 12px', fontSize: 11, fontWeight: 700 }}>
            Version 1 · Effective April 9, 2026
          </span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Scroll to read before signing</span>
        </div>

        {/* Agreement scroll box */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            height: 280, overflowY: 'auto', border: '1.5px solid #e2e8f0',
            borderRadius: 10, padding: '16px 18px', fontSize: 12,
            color: '#374151', lineHeight: 1.75, background: '#f8fafc',
            whiteSpace: 'pre-wrap', marginBottom: 16,
            fontFamily: 'monospace',
          }}
        >
          {PM_PROJECT_AGREEMENT_TEXT}
        </div>

        {/* Scroll indicator */}
        {!scrolledToBottom && (
          <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, marginBottom: 12, textAlign: 'center' }}>
            Scroll to the bottom to enable signing
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            {error}
          </div>
        )}

        {/* Typed name */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Full legal name (e-signature) <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="Type your full legal name"
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
          />
        </div>

        {/* Position */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Position / Title <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Property Manager, CEO"
            value={signerPosition}
            onChange={e => setSignerPosition(e.target.value)}
          />
        </div>

        {/* Checkbox */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20 }}>
          <input
            id="pm-proj-agree"
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            disabled={!scrolledToBottom}
            style={{ marginTop: 3, width: 16, height: 16, cursor: scrolledToBottom ? 'pointer' : 'not-allowed' }}
          />
          <label htmlFor="pm-proj-agree" style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.5, cursor: scrolledToBottom ? 'pointer' : 'default' }}>
            I have read and agree to the CoreBids Property Manager Project-Specific Agreement (Version 1, April 9, 2026). I confirm I have authority to award this project and fund escrow. <span style={{ color: '#dc2626' }}>*</span>
          </label>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: 10, background: '#f9fafb', fontSize: 14, cursor: 'pointer', fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={!canSign || saving}
            style={{
              flex: 2, padding: '12px', borderRadius: 10, border: 'none',
              background: canSign && !saving ? BRAND_GREEN : '#94a3b8',
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: canSign && !saving ? 'pointer' : 'not-allowed',
              opacity: canSign && !saving ? 1 : 0.7,
            }}
          >
            {saving ? 'Signing...' : 'Sign & Award Project'}
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 14 }}>
          Your name, timestamp, IP address, and browser fingerprint are recorded as your legally binding electronic signature.
        </p>
      </div>
    </div>
  );
};

export default PMProjectAgreementModal;
// CB:END pm-project-agreement-modal
